import {
  Button,
  ButtonLevel,
  ButtonSize,
  FieldHelper,
  Label,
  ListBox,
  type ListBoxProps,
  Popover,
  type PopoverProps,
  composeTailwindRenderProps,
} from '@vizit/atoms';
import { CheckIcon, ChevronDownIcon } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import type React from 'react';
import {
  SelectValue as AriaSelectValue,
  Collection,
  type ListBoxItemProps,
  Select,
  type SelectProps,
  type SelectValueProps,
  composeRenderProps,
} from 'react-aria-components';

interface SelectLabelProps {
  /**
   * The label for the select.
   */
  label?: React.ReactNode;

  /**
   * Whether the select is required.
   *
   * @optional
   * @type boolean
   */
  isRequired?: boolean;

  /**
   * Helper text to display below the select.
   *
   * @optional
   * @type string
   */
  helperText?: React.ReactNode;
}

interface CustomSelectValueProps<T extends object> extends SelectValueProps<T> {
  /**
   * The placeholder text to display when no item is selected
   * @optional
   * @type string
   */
  placeholder?: string;
}

const _Select = Select;

const SelectCollection = Collection;

function _SelectValue<T extends object>(props: CustomSelectValueProps<T>) {
  const { className, placeholder, ...other } = props;

  return (
    <AriaSelectValue
      className={composeTailwindRenderProps(
        className,
        'data-[placeholder]:text-placeholder data-[placeholder]:font-regular',
      )}
      {...other}
    >
      {({ isPlaceholder }) => (isPlaceholder ? placeholder : null)}
    </AriaSelectValue>
  );
}

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof Button> {}

function SelectTrigger(props: SelectTriggerProps) {
  const { children, className, isDisabled, ...other } = props;

  return (
    <Button
      className={cx(
        'flex h-20 items-center justify-between outline-none px-6 rounded-none',
        'w-full border border-primary bg-white text-md text-gray-light-900',
        // Add focus-within:px-[11px] to reduce the horizontal padding by 1px when the border grows to 2px (11px instead of 12px)
        // Without this, the content / placeholder would shift by 1px on popup expansion
        '[&[aria-expanded=true]]:border-2 [&[aria-expanded=true]]:border-brand-primary [&[aria-expanded=true]]:px-[11px] [&[aria-expanded=true]>.vzt-end-icon]:rotate-180',
        'data-[hovered]:bg-white',
        'pointer-events-auto data-[disabled]:cursor-not-allowed data-[disabled]:bg-disabled-subtle data-[disabled]:border-disabled data-[disabled]:text-disabled',
        className,
      )}
      isDisabled={isDisabled}
      size={ButtonSize.Lg}
      level={ButtonLevel.Secondary}
      {...other}
    >
      {(values) => (
        <>
          <span className="flex-1 min-w-0 text-left truncate">
            {typeof children === 'function' ? children(values) : children}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            size={16}
            className="vzt-end-icon ml-auto shrink-0 text-gray-utility-400 transition-transform duration-200"
          />
        </>
      )}
    </Button>
  );
}

function SelectItem(props: ListBoxItemProps) {
  const { className, children: childrenProp, ...other } = props;

  return (
    <ListBox.Item className={cx('relative', className)} {...other}>
      {composeRenderProps(childrenProp, (children, { isSelected }) => (
        <>
          {children}
          <span className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center">
            {isSelected && <CheckIcon size={16} color={colors.brand['600']} />}
          </span>
        </>
      ))}
    </ListBox.Item>
  );
}

function SelectPopover(props: PopoverProps) {
  const { className, offset = 4, ...other } = props;

  return (
    <Popover
      className={composeTailwindRenderProps(
        className,
        cx('w-[--trigger-width] min-w-[128px] overflow-y-auto shadow-md outline-none'),
      )}
      offset={offset}
      placement="bottom start"
      {...other}
    />
  );
}

function SelectContent<T extends object>(props: ListBoxProps<T>) {
  const { className, children, ...other } = props;

  return (
    <ListBox className={composeTailwindRenderProps(className, 'py-2')} {...other}>
      {children}
    </ListBox>
  );
}

/**
 * Helper component to add a label to a select
 */
function SelectFieldLabel({ label, isRequired }: SelectLabelProps) {
  return (
    <>
      {label && (
        <div className="flex flex-row items-baseline justify-between">
          {label && <Label isRequired={isRequired}>{label}</Label>}
        </div>
      )}
    </>
  );
}

/**
 * Component to display helper text below the select field
 */
function SelectHelperText(props: React.ComponentProps<typeof FieldHelper>) {
  const { children, ...other } = props;

  if (!children) return null;

  return <FieldHelper {...other}>{children}</FieldHelper>;
}

export {
  _Select as Select,
  SelectCollection,
  SelectContent,
  SelectFieldLabel,
  SelectHelperText,
  SelectItem,
  SelectPopover,
  SelectTrigger,
  _SelectValue as SelectValue,
};
export type { SelectLabelProps, SelectProps };
